/**
 * Ingest-monitoring fixtures, behind the shared mock gate.
 *
 * Scenarios (`VITE_ALERTS_MOCK_SCENARIO`):
 *   default      6h window, sub-daily buckets, trend gaps, an extreme clock-skew
 *                outlier in one stage, component + truncated scope breakdowns
 *   empty        zero accepted alerts, with a last-seen acceptance
 *   fallback-tz  daily buckets and a single valid bucket (no trend yet)
 *   unavailable  invalid monitoring configuration (NOT zero data)
 *   failure      403, the staff gate
 *   truncated    429 with Retry-After
 */
import { AlertApiError } from "@/alerts/api/envelope.ts";
import { MONITORING_STAGE_ORDER } from "@/alerts/types/alerts-monitoring.ts";
import type {
  AlertMonitoringComponentRow,
  AlertMonitoringParams,
  AlertMonitoringResponse,
  AlertMonitoringStageCode,
  AlertMonitoringStageMetric,
  AlertMonitoringSummary,
  AlertMonitoringTrendPoint,
} from "@/alerts/types/alerts-monitoring.ts";

const env = import.meta.env as Record<string, string | undefined>;
const scenario = (): string => env.VITE_ALERTS_MOCK_SCENARIO || "default";

const T0 = Date.parse("2026-08-04T12:00:00Z");
const iso = (offsetMinutes: number): string => new Date(T0 + offsetMinutes * 60_000).toISOString();

const share = (value: number, total: number): number | null => (total > 0 ? value / total : null);

const summary = (accepted: number): AlertMonitoringSummary => {
  const complete = Math.round(accepted * 0.86);
  const missing = Math.round(accepted * 0.09);
  const pending = Math.round(accepted * 0.04);
  const skew = accepted - complete - missing - pending;
  return {
    accepted_alert_count: accepted,
    complete_required_chain_count: complete,
    complete_required_chain_share: share(complete, accepted),
    missing_expected_timestamp_alert_count: missing,
    missing_expected_timestamp_alert_share: share(missing, accepted),
    pending_expected_timestamp_alert_count: pending,
    pending_expected_timestamp_alert_share: share(pending, accepted),
    clock_skew_alert_count: Math.max(0, skew),
    clock_skew_alert_share: share(Math.max(0, skew), accepted),
  };
};

const stage = (
  code: AlertMonitoringStageCode,
  total: number,
  p50: number | null,
  p95: number | null,
  notApplicable = 0,
): AlertMonitoringStageMetric => {
  const applicable = total - notApplicable;
  const missing = Math.round(applicable * 0.07);
  const pending = Math.round(applicable * 0.03);
  const skew = code === "capture_to_ingestion" ? Math.round(applicable * 0.02) : 0;
  const valid = applicable - missing - pending - skew;
  return {
    code,
    total_count: total,
    applicable_count: applicable,
    not_applicable_count: notApplicable,
    valid_count: valid,
    missing_count: missing,
    clock_skew_count: skew,
    pending_count: pending,
    valid_share_of_applicable: share(valid, applicable),
    missing_share_of_applicable: share(missing, applicable),
    clock_skew_share_of_applicable: share(skew, applicable),
    pending_share_of_applicable: share(pending, applicable),
    p50_ms: p50,
    p95_ms: p95,
  };
};

/** Stage p95 values chosen so one stage is a wild outlier — each needs its own scale. */
const STAGES = (accepted: number): AlertMonitoringStageMetric[] => [
  stage("model_inference", accepted, 180, 420),
  // A broken edge clock: seconds, not milliseconds. On a shared scale this
  // would flatten every other stage to a flat line.
  stage("capture_to_ingestion", accepted, 2_400, 486_000),
  stage("ingestion_delay", accepted, 90, 260),
  // Live publication is not applicable for records still inside the grace window.
  stage("live_publication", accepted, 1_200, 4_800, Math.round(accepted * 0.18)),
];

const points = (
  count: number,
  bucketMinutes: number,
  base: number,
  { gaps = true, single = false }: { gaps?: boolean; single?: boolean } = {},
): AlertMonitoringTrendPoint[] =>
  Array.from({ length: single ? 1 : count }, (_, i) => {
    // A bucket with no valid samples has a NULL p95 — a gap in the line, which
    // must never be drawn as a zero.
    const empty = gaps && !single && (i === 3 || i === 7);
    const total = empty ? 0 : 40 + ((i * 13) % 25);
    return {
      start_time: iso(-(count - i) * bucketMinutes),
      end_time: iso(-(count - i - 1) * bucketMinutes),
      p95_ms: empty ? null : base + ((i * 37) % 220),
      total_count: total,
      applicable_count: total,
      not_applicable_count: 0,
      valid_count: empty ? 0 : total - 2,
      missing_count: empty ? 0 : 2,
      clock_skew_count: 0,
      pending_count: 0,
    };
  });

const COMPONENTS = (accepted: number): AlertMonitoringComponentRow[] =>
  [
    { source_component: "edge-agent", factor: 0.55, missingRate: 0.18 },
    { source_component: "platform-ingest", factor: 0.3, missingRate: 0.04 },
    { source_component: "backfill-job", factor: 0.15, missingRate: 0.31 },
  ].map(({ source_component, factor, missingRate }) => {
    const count = Math.round(accepted * factor);
    const missing = Math.round(count * missingRate);
    return {
      ...summary(count),
      missing_expected_timestamp_alert_count: missing,
      missing_expected_timestamp_alert_share: share(missing, count),
      source_component,
      stages: STAGES(count),
    };
  });

const latency = () => new Promise((resolve) => setTimeout(resolve, 220));

export const mockAlertMonitoring = async (
  params: AlertMonitoringParams,
): Promise<AlertMonitoringResponse> => {
  await latency();
  const s = scenario();

  if (s === "failure") throw new AlertApiError("Staff access is required.", 403);
  if (s === "truncated") throw new AlertApiError("Too many requests.", 429, 45);
  if (s === "unavailable") {
    throw new AlertApiError("Monitoring configuration is invalid.", 503, null, {
      code: "alert_monitoring_configuration_invalid",
    });
  }

  const accepted = s === "empty" ? 0 : 1_284;
  const daily = s === "fallback-tz";
  const bucketSeconds = daily ? 86_400 : 900;

  return {
    as_of: iso(0),
    // Present even with zero accepted in the period — "nothing in this window"
    // is a different statement from "nothing, ever".
    scope_latest_accepted_at: s === "empty" ? iso(-4_320) : iso(-3),
    scope: {
      level: params.location_id ? "location" : "platform",
      organization_id: params.organization_id ?? null,
      location_id: params.location_id ?? null,
      machine_ids: params.machine_ids ?? [],
      device_mxids: params.device_mxids ?? [],
      source_components: params.source_components ?? [],
      type_in: params.type_in ?? [],
    },
    period: {
      start_time: params.start_time,
      end_time: params.end_time ?? iso(0),
      duration_seconds: 21_600,
      time_basis: "platform_accepted_at",
    },
    coverage: { live_expected_from: iso(-10_080), live_grace_seconds: 900 },
    summary: summary(accepted),
    stages: STAGES(accepted),
    breakdown: {
      dimension: accepted === 0 ? null : "machine",
      items:
        accepted === 0
          ? []
          : [
              { id: 12, label: "EH-740", factor: 0.42, missingRate: 0.22 },
              { id: 13, label: "EH-741", factor: 0.33, missingRate: 0.08 },
              { id: 14, label: "EH-742", factor: 0.25, missingRate: 0.03 },
            ].map(({ id, label, factor, missingRate }) => {
              const count = Math.round(accepted * factor);
              const missing = Math.round(count * missingRate);
              return {
                ...summary(count),
                missing_expected_timestamp_alert_count: missing,
                missing_expected_timestamp_alert_share: share(missing, count),
                organization_id: 6,
                organization_label: "Sigma Lithium",
                location_id: 7,
                location_label: "North Pit",
                machine_id: id,
                machine_label: label,
                device_mxid: null,
                capture_to_ingestion_p95_ms: 3_400 + id * 120,
                live_publication_missing_count: Math.round(count * 0.05),
                live_publication_missing_share: 0.05,
              };
            }),
      // Only the top entities — the UI must say so rather than imply a full ranking.
      truncated: accepted > 0,
    },
    component_breakdown: accepted === 0 ? [] : COMPONENTS(accepted),
    trend: {
      bucket_seconds: bucketSeconds,
      series: MONITORING_STAGE_ORDER.map((code) => ({
        stage: code,
        points:
          accepted === 0
            ? []
            : points(daily ? 7 : 12, daily ? 1_440 : 15, code === "capture_to_ingestion" ? 2_400 : 160, {
                single: daily,
              }),
      })),
    },
  };
};
